<template>
  <div class="charge-string-input">
    <div class="cs-segments" @paste.prevent="onPaste">
      <template v-for="(segment, i) in segments">
        <div :key="segment.key" class="cs-segment">
          <select
            v-if="segment.key === 'object' && objectOptions.length"
            :ref="`seg${i}`"
            :value="parts[i]"
            :disabled="disabled"
            :aria-label="segment.label"
            class="cs-box cs-select"
            :class="{ complete: parts[i].length === segment.length }"
            @change="onSelect(i, $event)"
          >
            <option value="" disabled>Select</option>
            <option v-for="code in objectOptions" :key="code.code" :value="code.code">{{ code.code }} - {{ code.description }}</option>
          </select>
          <input
            v-else
            :ref="`seg${i}`"
            :value="parts[i]"
            :maxlength="segment.length"
            :style="{ width: segment.length * 11 + 18 + 'px' }"
            :aria-label="`${segment.label} (${segment.length} digits)`"
            :placeholder="'0'.repeat(segment.length)"
            :disabled="disabled"
            inputmode="numeric"
            class="cs-box"
            :class="{ complete: parts[i].length === segment.length }"
            @input="onInput(i, $event)"
            @keydown="onKeydown(i, $event)"
            @keyup.enter="$emit('enter')"
          />
          <small class="cs-label">{{ segment.label }}</small>
        </div>
        <span v-if="i < segments.length - 1" :key="`${segment.key}-dash`" class="cs-dash">-</span>
      </template>
    </div>
    <charge-string-compare
      class="cs-preview"
      :current="preview"
      :invalid="overflow ? overflow.digits : ''"
      :invalid-label="overflow && overflow.source === 'paste' ? 'Pasted code (invalid)' : 'Saved code (invalid)'"
    >
      <template #current-note>
        <span class="cs-count" :class="{ done: isComplete && !overflow }">{{ digitCount }} of {{ totalLength }} digits</span>
      </template>
      <!-- A named slot is only rendered when the compare box shows it; the v-if guards too-short and empty codes. -->
      <template #default>
        <template v-if="overflow">
          <div>
            <strong>Too many digits.</strong>
            {{ overflow.source === 'paste' ? 'The pasted charge string' : 'The saved charge string' }} has {{ overflow.digits.length }} digits, but only
            {{ totalLength }} fit. The extra {{ overflow.dropped.length === 1 ? 'digit is' : 'digits are' }} underlined in the {{ overflow.source === 'paste' ? 'pasted' : 'saved' }} code above.
          </div>
          <div>The extra digit could be in any section. Check each one against the correct charge string before saving.</div>
        </template>
      </template>
    </charge-string-compare>
  </div>
</template>

<script>
import ChargeStringCompare from './ChargeStringCompare.vue';
import { SEGMENTS, TOTAL_LENGTH, splitChargeString, chargeStringOverflow } from './chargeString';

// Re-exported so existing imports from this file keep working.
export { SEGMENTS, splitChargeString, isValidChargeString, hasInvalidChargeString, formatChargeString } from './chargeString';

// Digits that don't fit in the boxes, tagged with where they came from.
function findOverflow(value, source) {
  const overflow = chargeStringOverflow(value);
  return overflow && { ...overflow, source };
}

export default {
  name: 'ChargeStringInput',
  components: { ChargeStringCompare },
  props: {
    // Digits only, no dashes: that is how charge strings are stored.
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // Active object codes ({ code, description }); when given, Object is picked from a list.
    objectCodes: { type: Array, default: () => [] },
  },

  data() {
    return {
      segments: SEGMENTS,
      parts: splitChargeString(this.value),
      // Stays up for the whole edit so the dropped digits aren't forgotten once a box is changed.
      overflow: findOverflow(this.value, 'saved'),
    };
  },

  computed: {
    totalLength() {
      return TOTAL_LENGTH;
    },
    digitCount() {
      return this.parts.join('').length;
    },
    isComplete() {
      return this.digitCount === this.totalLength;
    },
    // Only 4-digit codes fit the Object section; keep the current value even if it is no longer listed.
    objectOptions() {
      const options = this.objectCodes.filter((code) => /^\d{4}$/.test(String(code.code)));
      const current = this.parts[SEGMENTS.findIndex((s) => s.key === 'object')];
      if (current && !options.some((code) => String(code.code) === current)) {
        options.unshift({ code: current, description: 'not in the object code list' });
      }
      return options;
    },
    preview() {
      return SEGMENTS.map((segment, i) => this.parts[i] || '#'.repeat(segment.length)).join('-');
    },
  },

  watch: {
    value(value) {
      if (value !== this.parts.join('')) {
        this.parts = splitChargeString(value);
        this.overflow = findOverflow(value, 'saved');
      }
    },
  },

  mounted() {
    const first = this.parts.findIndex((part, i) => part.length < SEGMENTS[i].length);
    this.focus(first === -1 ? SEGMENTS.length - 1 : first);
  },

  methods: {
    focus(i) {
      const el = this.$refs[`seg${i}`] && this.$refs[`seg${i}`][0];
      if (el) el.focus();
    },

    emit() {
      this.$emit('input', this.parts.join(''));
      this.$emit('complete', this.isComplete);
    },

    onInput(i, event) {
      const digits = event.target.value.replace(/\D/g, '');
      const length = SEGMENTS[i].length;
      if (digits.length > length) {
        // Typing or autofill overflowed: spread the rest into the next sections.
        this.fillFrom(i, digits);
        return;
      }
      this.$set(this.parts, i, digits);
      event.target.value = digits;
      this.emit();
      if (digits.length === length && i < SEGMENTS.length - 1) this.focus(i + 1);
    },

    onSelect(i, event) {
      this.$set(this.parts, i, event.target.value);
      this.emit();
      if (i < SEGMENTS.length - 1) this.focus(i + 1);
    },

    onKeydown(i, event) {
      const el = event.target;
      if (event.key === 'Backspace' && el.selectionStart === 0 && el.selectionEnd === 0 && i > 0) {
        event.preventDefault();
        this.focus(i - 1);
      } else if (event.key === 'ArrowLeft' && el.selectionStart === 0 && i > 0) {
        event.preventDefault();
        this.focus(i - 1);
      } else if (event.key === 'ArrowRight' && el.selectionStart === el.value.length && i < SEGMENTS.length - 1) {
        event.preventDefault();
        this.focus(i + 1);
      } else if (event.key === '-' ) {
        event.preventDefault();
        if (i < SEGMENTS.length - 1) this.focus(i + 1);
      }
    },

    // Pasting "123-12345-..." or 33 plain digits fills every section.
    onPaste(event) {
      const text = (event.clipboardData || window.clipboardData).getData('text');
      const start = this.segmentIndexOf(event.target);
      const digits = text.replace(/\D/g, '');
      const dropped = this.fillFrom(start === -1 ? 0 : start, digits);
      if (dropped) {
        this.overflow = { digits, dropped, source: 'paste' };
      } else if (this.isComplete) {
        // A clean paste that fills every box replaces whatever was too long before.
        this.overflow = null;
      }
    },

    segmentIndexOf(el) {
      return SEGMENTS.findIndex((segment, i) => this.$refs[`seg${i}`] && this.$refs[`seg${i}`][0] === el);
    },

    fillFrom(start, digits) {
      let rest = digits;
      let i = start;
      for (; i < SEGMENTS.length && rest.length; i++) {
        this.$set(this.parts, i, rest.slice(0, SEGMENTS[i].length));
        rest = rest.slice(SEGMENTS[i].length);
      }
      this.emit();
      this.focus(Math.min(i, SEGMENTS.length - 1));
      // Digits that ran past the last box.
      return rest;
    },
  },
};
</script>

<style lang="scss" scoped>
.cs-segments {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2px;
}

.cs-segment {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cs-box {
  height: 32px;
  padding: 0 6px;
  border: 1px solid #d8d6de;
  border-radius: 5px;
  background: #fff;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px;
  letter-spacing: 1px;
  text-align: center;
  color: #5e5873;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.cs-box::placeholder {
  color: #d0cfd6;
}

.cs-box:focus {
  outline: none;
  border-color: #a51c30;
  box-shadow: 0 0 0 2px rgba(165, 28, 48, 0.15);
}

.cs-box.complete {
  border-color: #28c76f;
}

.cs-label {
  margin-top: 2px;
  font-size: 10px;
  color: #82868b;
}

.cs-dash {
  padding-top: 5px;
  font-weight: 700;
  color: #82868b;
}

.cs-select {
  width: 190px;
  padding: 0 4px;
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0;
  text-align: left;
}

.cs-preview {
  margin-top: 6px;
}

.cs-count {
  font-family: Montserrat, Helvetica, Arial, sans-serif;
  font-size: 11px;
  color: #c46a12;
}

.cs-count.done {
  color: #1f9d57;
}
</style>
